export class SearchResults {
  public totalCount = 0;
  public agentCount = 0;
  public agents?: SearchResult[];
  public officeCount = 0;
  public offices?: SearchResult[];
  public franchisorCount = 0;
  public franchisors?: SearchResult[];
  public categoryOrder: Array<'agents' | 'offices' | 'franchisors'> = ['agents', 'offices', 'franchisors'];
}

export class SearchResult {
  public objectID?: string;
  public name?: string;
  public firstName?: string;
  public lastName?: string;
  public nickname?: string;
  public email?: string;
  public photoUrl?: string;
  public licenceNumbers?: string[];
  public licences?: SearchResultLicence[];
  public group?: {
    id?: string;
    name?: string;
  };
  public suburb?: string;
  public postcode?: string;
  public isSuperGroup?: boolean;

  public _highlightResult?: {
    name: HighlightResult;
    email: HighlightResult;
    licenceNumbers: HighlightResult[];
    suburb: HighlightResult;
    postcode: HighlightResult;
    isSuperGroup: HighlightResult;
  }
}

export class SearchResultLicence {
  public licenceNumber?: string;
  public region?: string;
}

export class HighlightResult {
  public value?: string;
  public matchLevel?: string;
  public fullyHighlighted?: boolean;
}

/**
 * Resolves the licences to display for a search result, preferring the richer
 * `licences` array (with region info) and falling back to the plain
 * `licenceNumbers` list. Duplicate licence numbers are removed so the same
 * number is never shown twice in the search results.
 */
export function getSearchResultLicences(result?: SearchResult | null): SearchResultLicence[] {
  const licences: SearchResultLicence[] = result?.licences?.length
    ? result.licences.filter((licence) => !!licence?.licenceNumber || !!licence?.region)
    : (result?.licenceNumbers?.map((licenceNumber) => ({ licenceNumber })) ?? []);

  const byKey = new Map<string, SearchResultLicence>();

  for (const licence of licences) {
    const number = licence.licenceNumber?.trim();
    const region = licence.region?.trim();

    // Key on the licence number so the same number is never shown twice, even
    // when one copy carries a region and another doesn't. Fall back to the
    // region for the rare region-only entry.
    const key = number ? `number:${number.toLowerCase()}` : `region:${region?.toLowerCase() ?? ''}`;
    const existing = byKey.get(key);

    if (!existing) {
      byKey.set(key, licence);
    } else if (!existing.region && region) {
      // Prefer the copy that carries a region for a clearer display.
      byKey.set(key, licence);
    }
  }

  return Array.from(byKey.values());
}

export interface SearchPageResult {
  agents: SearchResult[];
  agentTotal: number;
  agentNbPages: number;
  offices: SearchResult[];
  officeTotal: number;
  officeNbPages: number;
  franchisors: SearchResult[];
  franchisorTotal: number;
  franchisorNbPages: number;
}
